import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface PanelHeaderButtonProps {
  /** Named to assistive technology, and shown as the hover title. */
  label: string;
  children: ReactNode;
  onClick?: () => void;
  /**
   * Renders a link instead of a button. For an action the browser has to
   * follow rather than one this app handles — a download the server named when
   * it signed the URL, which no click handler can reproduce.
   */
  href?: string;
}

/**
 * A control in a panel's 52px header strip: quiet 28px square, soft hover.
 *
 * One definition for every glyph in that strip, whichever panel it belongs to
 * and whether it acts or navigates, so four controls in one header are four of
 * the same control rather than three of one and one of another.
 */
export function PanelHeaderButton({ label, children, onClick, href }: PanelHeaderButtonProps) {
  const className = cn(
    'inline-flex size-7 shrink-0 items-center justify-center rounded-[7px]',
    'text-[#696969] transition-colors hover:bg-[#F1EFEA] hover:text-[#181818]',
  );

  if (href !== undefined) {
    return (
      <a href={href} aria-label={label} title={label} className={className}>
        {children}
      </a>
    );
  }

  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} className={className}>
      {children}
    </button>
  );
}
